import { motion } from "framer-motion";
import { Phone, MapPin, Clock, Instagram, MessageCircle } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

export default function Contact({ t }) {
  const contactInfo = [
    {
      Icon: Phone,
      label: t.contact.labels.phone,
      value: t.contact.values.phone,
      href: "tel:+994555525001",
    },
    {
      Icon: MapPin,
      label: t.contact.labels.address,
      value: t.contact.values.address,
      href: "https://maps.google.com/?q=Baku+Marriott+Hotel+Boulevard,Baku,Azerbaijan",
    },
    {
      Icon: Clock,
      label: t.contact.labels.hours,
      value: t.contact.values.hours,
      href: null,
    },
    {
      Icon: Instagram,
      label: t.contact.labels.instagram,
      value: t.contact.values.instagram,
      href: "https://www.instagram.com/orkhan__babayev",
    },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-32 bg-[#0F0F0F]"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <p className="text-xs tracking-[0.3em] sm:tracking-[0.38em] uppercase text-[#D4AF37] font-body mb-4">
            {t.contact.sectionTitle}
          </p>
          <h2
            className="text-4xl md:text-5xl font-bold text-white font-heading mb-4"
            data-testid="contact-title"
          >
            {t.contact.headline}
          </h2>
          <p className="text-white/60 font-body text-sm tracking-widest italic">
            {t.contact.note}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Contact details */}
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-1"
            data-testid="contact-info"
          >
            {contactInfo.map((info, i) => {
              const { Icon } = info;
              const inner = (
                <motion.div
                  variants={fadeUp}
                  className="flex items-start gap-5 py-5 border-b border-white/5 group"
                  data-testid={`contact-info-${i}`}
                >
                  <div className="shrink-0 w-10 h-10 border border-[#D4AF37]/25 flex items-center justify-center group-hover:bg-[#D4AF37]/10 transition-colors duration-300">
                    <Icon size={15} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-xs tracking-widest uppercase text-white/55 font-body mb-1">
                      {info.label}
                    </p>
                    <p className="text-white/75 font-body text-sm leading-relaxed">
                      {info.value}
                    </p>
                  </div>
                </motion.div>
              );

              return info.href ? (
                <a
                  key={i}
                  href={info.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:opacity-80 active:opacity-70 transition-opacity duration-300"
                >
                  {inner}
                </a>
              ) : (
                <div key={i}>{inner}</div>
              );
            })}
          </motion.div>

          {/* CTA card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            data-testid="contact-cta-area"
          >
            <div className="bg-[#1A1A1A] border border-white/10 p-6 sm:p-10">
              <p className="text-xs tracking-[0.28em] uppercase text-[#D4AF37] font-body mb-4">
                {t.contact.ctaCard.title}
              </p>
              <h3 className="text-3xl font-heading text-white mb-4">{t.contact.ctaCard.headline}</h3>
              <p className="text-white/65 font-body text-sm leading-relaxed mb-9">
                {t.contact.ctaCard.description}
              </p>

              <div className="flex flex-col gap-4">
                <a
                  href="https://wa.me/994555525001"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 px-8 py-4 bg-[#D4AF37] text-black text-xs tracking-[0.22em] uppercase font-body font-bold hover:bg-[#F4CF57] transition-colors duration-300"
                  data-testid="contact-whatsapp-cta"
                >
                  <MessageCircle size={16} />
                  {t.contact.ctaCard.whatsapp}
                </a>
                <a
                  href="tel:+994555525001"
                  className="flex items-center justify-center gap-3 px-8 py-4 border border-white/12 text-white/60 text-xs tracking-[0.22em] uppercase font-body font-medium hover:border-[#D4AF37]/40 hover:text-white transition-colors duration-300"
                  data-testid="contact-phone-cta"
                >
                  <Phone size={16} />
                  {t.contact.ctaCard.phone}
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
